// /projects — list view + "New project" dialog (T030).
//
// T271: adds a filter bar (name/key text search + quality-gate status select)
// above the project list. Filtering is client-side over the loaded SWR list.
//
// Data flow:
//   - `useSWR('/api/v1/projects', fetcher)` for the list (dedup, focus
//     revalidation, typed ApiError on the error channel).
//   - `mutate()` after a successful POST injects the freshly-created Project
//     so the list updates without a second round-trip.
//   - The dialog is the controlled `<Dialog>` primitive (native <dialog>
//     semantics underneath: focus trap, Esc-to-close, top-layer backdrop).
import { FormEvent, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { GitBranch, Plus, Search, X } from 'lucide-react';
import {
  ApiError,
  Project,
  ProjectCreate,
  fetcher,
  projectsApi,
} from '../lib/api';
import { useLicenseState } from '../lib/useLicenseState';
import {
  Button,
  Card,
  Dialog,
  DialogFooter,
  DialogHeader,
  EmptyState,
  ErrorState,
  FormField,
  Input,
  LoadingCard,
  PageHeader,
} from '../components/ui';

const PROJECTS_KEY = '/api/v1/projects';

type GateFilter = 'all' | 'passed' | 'failed' | 'error';

export function Projects() {
  const { data, error, isLoading, mutate } = useSWR<Project[]>(PROJECTS_KEY, fetcher);
  const { isExpired } = useLicenseState();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [gateFilter] = useState<GateFilter>('all');

  // Client-side filter: by name, slug, or repo_url.
  // Quality-gate status and language filtering require backend filter params
  // not yet exposed by GET /api/v1/projects — gateFilter placeholder is
  // wired in the select but does not narrow results until backend support lands.
  const filteredProjects = useMemo(() => {
    const projects = data ?? [];
    const q = filterText.toLowerCase().trim();
    if (!q && gateFilter === 'all') return projects;
    return projects.filter((p) => {
      if (q) {
        const hay = `${p.name} ${p.slug} ${p.repo_url}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data, filterText, gateFilter]);

  return (
    <div className="max-w-5xl mx-auto p-8 w-full">
      <PageHeader
        eyebrow="orbital · projects"
        title="Projects"
        description="Repos scanned by orbital-lint. Pick one to view its scans and issues."
        actions={
          <Button
            onClick={() => setDialogOpen(true)}
            disabled={isExpired}
            title={
              isExpired ? 'License required — upload a license to create projects' : undefined
            }
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            <span>New project</span>
          </Button>
        }
      />

      {/* T271: filter bar — text search + gate status */}
      {!isLoading && !error && (
        <ProjectFilterBar
          filterText={filterText}
          onFilterTextChange={setFilterText}
          totalCount={data?.length ?? 0}
          filteredCount={filteredProjects.length}
        />
      )}

      <ProjectsListBody isLoading={isLoading} error={error} projects={filteredProjects} />

      <NewProjectDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={async (created) => {
          // Optimistically inject the new project; SWR revalidates on next
          // focus/interval.
          await mutate((cur) => [...(cur ?? []), created], { revalidate: false });
          setDialogOpen(false);
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filter bar (T271)
// ---------------------------------------------------------------------------

interface FilterBarProps {
  readonly filterText: string;
  readonly onFilterTextChange: (v: string) => void;
  readonly totalCount: number;
  readonly filteredCount: number;
}

function ProjectFilterBar({
  filterText,
  onFilterTextChange,
  totalCount,
  filteredCount,
}: FilterBarProps) {
  const isFiltered = filterText.trim() !== '';
  return (
    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
      <div className="relative flex-1">
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-fg-muted pointer-events-none"
          aria-hidden="true"
        />
        <Input
          type="search"
          value={filterText}
          onChange={(e) => onFilterTextChange(e.target.value)}
          placeholder="Filter by name or key…"
          aria-label="Filter projects"
          className="pl-9 pr-8"
        />
        {isFiltered && (
          <button
            type="button"
            onClick={() => onFilterTextChange('')}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-fg-muted hover:text-fg transition-colors"
            aria-label="Clear filter"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        )}
      </div>
      {isFiltered && (
        <p className="text-xs text-fg-muted shrink-0">
          {filteredCount} of {totalCount} project{totalCount === 1 ? '' : 's'}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// List body
// ---------------------------------------------------------------------------

interface ListBodyProps {
  readonly isLoading: boolean;
  readonly error: unknown;
  readonly projects: Project[];
}

function ProjectsListBody({ isLoading, error, projects }: ListBodyProps) {
  if (isLoading) return <LoadingCard label="Loading projects…" />;
  if (error) {
    // 400 missing_org means no token and no dev-org header — api.ts already
    // redirects to /login on this error (T337); the hint below only shows in
    // the brief window before that navigation completes.
    const hint =
      error instanceof ApiError && error.code === 'missing_org'
        ? 'Set VITE_DEV_ORG_ID for local dev, or sign in — redirecting to /login…'
        : undefined;
    return <ErrorState error={error} hint={hint} />;
  }
  if (projects.length === 0) {
    return (
      <EmptyState
        title="No projects yet."
        description={
          <>
            Create a project to give the scanner a target. The scanner CLI uploads its
            bundles to <span className="orb-code">/api/v1/scans</span> against the project
            token printed at creation time.
          </>
        }
      />
    );
  }

  // Stable sort: newest first — matches "I just created one, where did it go?".
  const sorted = [...projects].sort((a, b) => b.created_at.localeCompare(a.created_at));

  return (
    <Card as="ul" className="divide-y divide-border">
      {sorted.map((p) => (
        <li key={p.id}>
          <Link
            to={`/projects/${p.id}`}
            className="flex items-center gap-4 px-5 py-4 hover:bg-bg-hover transition-colors"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2">
                <span className="font-display text-base font-semibold text-fg truncate">
                  {p.name}
                </span>
                <span className="orb-code text-xs">{p.slug}</span>
              </div>
              {p.repo_url ? (
                <div className="text-xs text-fg-subtle truncate mt-0.5">{p.repo_url}</div>
              ) : null}
            </div>
            <div
              className="flex items-center gap-1 text-xs text-fg-muted shrink-0"
              title="Default branch"
            >
              <GitBranch className="h-3.5 w-3.5" aria-hidden="true" />
              {p.default_branch}
            </div>
          </Link>
        </li>
      ))}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// New project dialog
// ---------------------------------------------------------------------------

interface DialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Called with the freshly-created project after a successful POST. */
  readonly onCreated: (created: Project) => void | Promise<void>;
}

function NewProjectDialog({ open, onClose, onCreated }: DialogProps) {
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [repoURL, setRepoURL] = useState('');
  const [defaultBranch, setDefaultBranch] = useState('main');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // The Dialog only mounts its body while `open`, so a fresh open already
  // starts with these initial values — no manual reset needed.

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    setErr(null);
    setSubmitting(true);
    try {
      const body: ProjectCreate = { slug: slug.trim(), name: name.trim() };
      const branch = defaultBranch.trim();
      const url = repoURL.trim();
      if (branch) body.default_branch = branch;
      if (url) body.repo_url = url;
      const created = await projectsApi.create(body);
      await onCreated(created);
    } catch (e2) {
      let msg: string;
      if (e2 instanceof ApiError) {
        msg = `${e2.code}: ${e2.message}`;
      } else if (e2 instanceof Error) {
        msg = e2.message;
      } else {
        msg = 'request failed';
      }
      setErr(msg);
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogHeader eyebrow="new · project" title="Add a project" />
      <form onSubmit={onSubmit} className="space-y-4">
        <FormField label="Name" htmlFor="np-name">
          <Input
            id="np-name"
            type="text"
            required
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="orbital-reg"
          />
        </FormField>
        <FormField label="Slug" htmlFor="np-slug" hint="Lowercase, used in URLs. Unique per org.">
          <Input
            id="np-slug"
            type="text"
            required
            // Hyphen must be escaped inside the class: some browsers compile
            // the `pattern` attribute's regex with the newer 'v' (Unicode
            // Sets) flag, which treats an unescaped trailing `-` in
            // `[a-z0-9-]` as an invalid character class rather than a
            // literal hyphen — throws a SyntaxError instead of validating
            // (T635 found this live: the create-project dialog logged the
            // exception to the console on every render once a value was
            // typed, though HTML5 form validation still degraded gracefully
            // since a throwing pattern is treated as "no constraint").
            pattern="[a-z0-9][a-z0-9\-]*"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="orbital-reg"
          />
        </FormField>
        <FormField label="Repo URL" htmlFor="np-repo" hint="Optional.">
          <Input
            id="np-repo"
            type="url"
            value={repoURL}
            onChange={(e) => setRepoURL(e.target.value)}
            placeholder="https://github.com/aphelion-holding/orbital-reg"
          />
        </FormField>
        <FormField
          label="Default branch"
          htmlFor="np-branch"
          hint="The branch that long-running scans are pinned to."
        >
          <Input
            id="np-branch"
            type="text"
            value={defaultBranch}
            onChange={(e) => setDefaultBranch(e.target.value)}
            placeholder="main"
          />
        </FormField>

        {err ? (
          <p role="alert" className="text-xs text-danger">
            {err}
          </p>
        ) : null}

        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" loading={submitting}>
            {submitting ? 'Creating…' : 'Create project'}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
